import React from 'react';
import { Language } from '../types';
import { translations } from '../translations';

interface HeroProps {
  onBookClick: () => void;
  onChartClick: () => void;
  lang?: Language;
}

const Hero: React.FC<HeroProps> = ({ onBookClick, onChartClick, lang = 'en' }) => {
  const t = (translations[lang] || translations.en).hero;

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto flex flex-col items-center">
        <div className="mb-6 flex items-center gap-4 opacity-0 animate-[fadeIn_0.8s_ease-out_0.2s_forwards]">
          <div className="h-px w-8 bg-amber-500/50" />
          <span className="text-amber-500 text-[10px] md:text-xs tracking-[0.6em] uppercase font-bold drop-shadow-[0_0_8px_rgba(245,158,11,0.4)]">
            {t.tag}
          </span>
          <div className="h-px w-8 bg-amber-500/50" />
        </div>

        <h1 className="flex flex-col items-center mb-10 group cursor-default">
          <span className="text-2xl md:text-4xl font-serif tracking-[0.8em] text-slate-400 font-light uppercase mb-2 md:mb-4 transition-all duration-700 group-hover:tracking-[1.2em] group-hover:text-amber-200">
            {t.subtitle}
          </span>
          <span className="relative inline-block text-6xl md:text-9xl font-serif font-bold leading-none py-2">
            <span className="absolute inset-0 blur-3xl bg-amber-500/20 rounded-full scale-110 opacity-30 transition-all duration-1000 group-hover:opacity-60 group-hover:scale-150" />
            
            <span className="relative z-10 inline-block text-transparent bg-clip-text bg-gradient-to-b from-white via-amber-100 to-amber-500 shimmer-text drop-shadow-[0_10px_10px_rgba(0,0,0,0.5)] transition-all duration-700 ease-out group-hover:-translate-y-3 group-hover:scale-105 group-hover:drop-shadow-[0_0_40px_rgba(245,158,11,0.6)]">
              {t.by}
            </span>
          </span>
        </h1>

        <p className="text-indigo-100/70 text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed font-light opacity-0 animate-[fadeIn_1s_ease-out_1s_forwards]">
          {t.desc}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 opacity-0 animate-[fadeIn_1s_ease-out_1.4s_forwards]">
          <button
            id="hero-btn-chart"
            onClick={onChartClick}
            className="group relative px-10 py-5 bg-amber-500 text-slate-950 font-bold tracking-[0.2em] uppercase transition-all overflow-hidden rounded-sm min-w-[240px]"
          >
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            <span className="relative z-10">{t.btnChart}</span>
          </button>
          <button
            id="hero-btn-book"
            onClick={onBookClick}
            className="group px-10 py-5 border border-indigo-500/50 text-indigo-100 hover:text-white hover:border-amber-400 font-bold tracking-[0.2em] uppercase transition-all rounded-sm backdrop-blur-md relative min-w-[240px]"
          >
            <div className="absolute inset-0 bg-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            {t.btnBook}
          </button>
        </div>
      </div>

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce opacity-30 flex flex-col items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.4em] font-bold text-indigo-400">Descend</span>
        <div className="w-5 h-8 border-2 border-indigo-400/50 rounded-full flex justify-center p-1">
          <div className="w-1 h-1.5 bg-amber-400 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;